// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { ClickableProps } from "@insite/mobius/Clickable";
import { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import { IconMemo, IconPresentationProps } from "@insite/mobius/Icon";
import EditIcon from "@insite/mobius/Icons/Edit2";
import { Clickable, DataTableCell, DataTableRow, Icon, VisuallyHidden } from "@insite/mobius/index";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Close from "@store/Library/Components/Toolkit/Icons/Close";
import TwoButtonModal from "@store/Overrides/Components/TwoButtonModal";
import { CustomerPoNumber } from "@store/Framework/Services/CustomerPoNumberService";
import PromotionalTag from "@store/Library/Components/Common/PromotionalTag";
import { DatePickerPresentationProps } from "@store/MobiusOverrides/DatePicker/DatePicker";
import useExpandProps from "@toolkit/Framework/Common/Hooks/useExpandProps";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import React from "react";
import { css } from "styled-components";

export interface ManageDefaultPOTableRowProps {
    customerPoNumber: CustomerPoNumber;
    setEditingPo: (customerPoNumber: CustomerPoNumber) => void;
    extendedStyles?: ManageDefaultPOTableRowStyles;
    overrideStyles?: ManageDefaultPOTableRowStyles;
    isEditing: boolean;
    onDelete: (poId: string) => void;
    isActive: boolean;
}
export interface ManageDefaultPOTableRowStyles {
    rowStyles?: DataTableRowProps;
    clickable: ClickableProps;
    buttonCell?: DataTableCellProps;
    editPoButton?: IconPresentationProps;
    removePoButton?: IconPresentationProps;
    datePicker?: DatePickerPresentationProps;
    floatingTagWrapper?: InjectableCss;
}

const manageDefaultPORowStyles: ManageDefaultPOTableRowStyles = {
    rowStyles: {
        css: css`
            position: relative;
        `,
    },
    floatingTagWrapper: {
        css: css`
            position: absolute;
            right: -5rem;
            top: 1rem;
        `,
    },
    buttonCell: {
        css: css`
            padding-inline: 0;
            display: flex;
            align-items: center;
            justify-content: space-evenly;
        `,
    },
    datePicker: {
        cssOverrides: {
            inputSelect: css`
                padding: ${pxToRem(2)} ${pxToRem(10)};
            `,
        },
    },
    removePoButton: {
        src: Close,
        size: 22,
        css: css`
            color: ${getColor("primary")};
            :hover {
                color: ${getColor("text.linkHover")};
            }
        `,
    },
    clickable: {
        css: css`
            transition: opacity 0.4s ease-in-out;
            &:disabled {
                cursor: not-allowed;
                opacity: 0.4;
            }
        `,
    },
    editPoButton: {
        src: EditIcon,
        size: 22,
        css: css`
            color: ${getColor("primary")};
            :hover {
                color: ${getColor("text.linkHover")};
            }
        `,
    },
};
const ManageDefaultPOTableRow: React.FC<ManageDefaultPOTableRowProps> = ({
    customerPoNumber,
    extendedStyles,
    overrideStyles,
    setEditingPo,
    isEditing,
    onDelete,
    isActive,
}) => {
    const poNumber = customerPoNumber.poNumber;
    const startDate = new Date(customerPoNumber.startDate);
    const endDate = customerPoNumber.endDate ? new Date(customerPoNumber.endDate) : undefined;
    const [isConfirmModalOpen, setIsConfirmModalOpen] = React.useState(false);
    const styles = useExpandProps(manageDefaultPORowStyles, extendedStyles, overrideStyles);

    return (
        <DataTableRow data-test-selector="Po_not_editing_row" {...styles.rowStyles}>
            <DataTableCell
                typographyProps={{
                    ellipsis: true,
                    css: css`
                        display: inline-block;
                        inline-size: 14rem;
                    `,
                }}
            >
                {poNumber}
            </DataTableCell>
            <DataTableCell>{startDate.toLocaleDateString()}</DataTableCell>
            <DataTableCell>{endDate ? endDate.toLocaleDateString() : ""}</DataTableCell>
            <DataTableCell>
                <StyledWrapper {...styles.buttonCell}>
                    <Clickable
                        disabled={isEditing}
                        onClick={() => setEditingPo(customerPoNumber)}
                        data-test-selector="PoOverride_editPoButton"
                        {...styles.clickable}
                    >
                        <Icon {...styles.editPoButton} />
                        <VisuallyHidden>{translate("edit this product")}</VisuallyHidden>
                    </Clickable>
                    <Clickable
                        disabled={isEditing}
                        onClick={() => setIsConfirmModalOpen(true)}
                        data-test-selector="PoOverride_removePoButton"
                        {...styles.clickable}
                    >
                        <IconMemo {...styles.removePoButton} />
                        <VisuallyHidden>{translate("Removed this product")}</VisuallyHidden>
                    </Clickable>
                </StyledWrapper>
                {isActive && (
                    <StyledWrapper {...styles.floatingTagWrapper}>
                        <PromotionalTag variant="active" tag="active" />
                    </StyledWrapper>
                )}
            </DataTableCell>
            <TwoButtonModal
                // eslint-disable-next-line spire/avoid-dynamic-translate
                messageText={translate("Are you sure you want to delete this default PO?")}
                submitTestSelector="PoOverride_confirmDeleteButton"
                cancelButtonText={translate("Cancel")}
                submitButtonText={translate("Confirm")}
                headlineText={translate("Delete PO")}
                modalIsOpen={isConfirmModalOpen}
                onCancel={() => setIsConfirmModalOpen(false)}
                onSubmit={() => onDelete(customerPoNumber.id)}
            />
        </DataTableRow>
    );
};

export default ManageDefaultPOTableRow;
